import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Root-level boundary so a single rendering error doesn't blank the whole
// app. Class component is required here — hooks can't catch render-phase
// errors from children.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("unhandled render error", error, info.componentStack);
  }

  handleReload = () => {
    // Full reload — resets state, re-fetches config, re-runs auth flow.
    window.location.reload();
  };

  override render() {
    if (this.state.error) {
      return (
        <div className="error-boundary">
          <div className="error-boundary-card">
            <h1>Something went wrong.</h1>
            <p className="muted">
              CaseThread hit an unexpected error. Your data is safe — this is
              a UI glitch. Refresh to recover.
            </p>
            <pre className="error-boundary-detail">
              {this.state.error.message}
            </pre>
            <button
              type="button"
              className="btn-primary"
              onClick={this.handleReload}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
